import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    OnInit,
    output,
    QueryList,
    signal,
    ViewChildren
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ComponentsModule, fadeIn, MessageType } from '../../../../lib';
import {
    Block,
    BlockType,
    ContextModelState,
    DataValidation,
    FrontendConfiguration,
    LayoutWidthBreakpoints,
    TemplateSlot
} from '../../interfaces';
import {
    BlockIdPipe,
    MarkDoneBlocksPipe,
    TargetSlotValidationsPipe,
    RequiredMarkerPipe,
    TranslationKeyPipe,
    SlotTypeCssClassPipe,
    VisibleQuestionBlocksPipe,
    CachePipe,
    MultiSlotLayoutGapPipe
} from '../../pipes';
import { QuestionFlowService, ContextModelService, DataSourceService, SlotService } from '../../services';
import { HSP_CONFIGURATION, INITIAL_CONTEXT_MODEL_STATE } from '../../data';
import { TemplateType } from '../../enums';
import { TemplateSlotComponent, TemplateCombinedSlotComponent } from '../slot';
import { LayoutDirective, ResizeObserverDirective } from '../../directives';
import { FormDirective } from '../../directives/form.directive';
import { FlowTimelineComponent } from '../flow-timeline/flow-timeline.component';
import { DataSourcesService } from '../../../v-api';
import { ACCORDION_LAYOUT_WIDTH_BREAKPOINTS, ACCORDION_TIMELINE_MARGIN_PX } from '../../consts';

@Component({
    selector: 'ca-question-flow',
    imports: [
        TranslateModule,
        ComponentsModule,
        FlowTimelineComponent,
        TemplateSlotComponent,
        TemplateCombinedSlotComponent,
        LayoutDirective,
        ResizeObserverDirective,
        TranslationKeyPipe,
        BlockIdPipe,
        MarkDoneBlocksPipe,
        MatButtonModule,
        MatIconModule,
        FormDirective,
        TargetSlotValidationsPipe,
        RequiredMarkerPipe,
        VisibleQuestionBlocksPipe,
        SlotTypeCssClassPipe,
        CachePipe,
        MultiSlotLayoutGapPipe
    ],
    animations: [fadeIn],
    providers: [QuestionFlowService, ContextModelService, DataSourceService, DataSourcesService, SlotService],
    templateUrl: './question-flow.component.html',
    styleUrl: './question-flow.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuestionFlowComponent implements OnInit, AfterViewInit {
    readonly templateType = TemplateType;
    readonly blockType = BlockType;
    devMode = input<boolean>(false);
    configuration = input<FrontendConfiguration>(HSP_CONFIGURATION);
    initialContextModel = input<ContextModelState>(INITIAL_CONTEXT_MODEL_STATE);
    hideActionButtons = input<boolean>(false);
    hideTimeline = input<boolean>(false);
    markAllAsTouched = input<boolean>(false);
    submissionEffect = input<((currentState: ContextModelState) => Promise<any>) | undefined>(undefined);
    showResults = output<void>();
    contextChange = output<ContextModelState>();
    invalidConfigurationChange = output<boolean>();
    validityChange = output<boolean>();
    isLoading = signal(false);
    hasError = signal(false);
    readonly flowBreakpoints = computed<LayoutWidthBreakpoints>(() => {
        // The accordion element is indented by a fixed margin when the timeline is visible.
        const offset = this.hideTimeline() ? 0 : ACCORDION_TIMELINE_MARGIN_PX;
        return {
            xSmallMax: ACCORDION_LAYOUT_WIDTH_BREAKPOINTS.xSmallMax + offset,
            smallMax: ACCORDION_LAYOUT_WIDTH_BREAKPOINTS.smallMax + offset,
            mediumMax: ACCORDION_LAYOUT_WIDTH_BREAKPOINTS.mediumMax + offset,
            largeMax: ACCORDION_LAYOUT_WIDTH_BREAKPOINTS.largeMax + offset
        };
    });
    @ViewChildren(FormDirective) private readonly formDirectivesList!: QueryList<FormDirective>;
    private readonly formDirectivesSignal = signal<readonly FormDirective[]>([]);

    service = inject(QuestionFlowService);
    contextModel = inject(ContextModelService);
    private slotService = inject(SlotService);
    readonly messageTypes = MessageType;
    private readonly destroyRef = inject(DestroyRef);
    private changeDetectorRef = inject(ChangeDetectorRef);
    private readonly onConfigurationChange = effect(() => {
        if (this.configuration()) {
            this.init();
        }
    });

    private readonly onInitialContextModelChange = effect(() => {
        if (this.initialContextModel()) {
            this.init();
        }
    });

    private readonly onInvalidConfigurationChange = effect(() => {
        this.invalidConfigurationChange.emit(this.service.invalidConfiguration());
    });

    private readonly onContextStateVersionChange = effect(() => {
        const version = this.service.contextStateVersion();
        if (version > 0) {
            this.contextChange.emit(this.service.contextModelCurrentState);
        }
    });

    private readonly onFormValidityChange = effect(onCleanup => {
        const directives = this.formDirectivesSignal();
        // Re-evaluate when context model changes (question-block validations depend on it)
        this.service.contextStateVersion();

        const emitValidity = () => {
            const formsValid = directives.every(fd => fd.form.valid);
            const hasBlockError = this.hasAnyQuestionBlockValidationError();
            this.validityChange.emit(formsValid && !hasBlockError);
        };

        const subscriptions = directives.map(d => d.form.statusChanges.pipe(startWith(d.form.status)).subscribe(() => emitValidity()));

        emitValidity();

        onCleanup(() => subscriptions.forEach(s => s.unsubscribe()));
    });

    ngOnInit() {
        this.init();
    }

    ngAfterViewInit() {
        this.formDirectivesSignal.set(this.formDirectivesList.toArray());
        this.formDirectivesList.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.formDirectivesSignal.set(this.formDirectivesList.toArray());
        });
    }

    back(form: FormGroup, accordionBlock: Block) {
        this.service.onBack(accordionBlock);
    }

    next(form: FormGroup, accordionBlock: Block) {
        form.updateValueAndValidity();
        form.markAllAsTouched();
        if (!form.valid || this.hasQuestionBlockValidationError(accordionBlock)) {
            return;
        }
        const submissionEffect = this.submissionEffect();
        if (!submissionEffect) {
            this.hasError.set(false);
            this.service.onNext(accordionBlock);
            return;
        }
        this.isLoading.set(true);
        submissionEffect(this.contextModel.currentState)
            .then(() => {
                this.isLoading.set(false);
                this.hasError.set(false);
                this.service.onNext(accordionBlock);
                this.changeDetectorRef.detectChanges();
            })
            .catch(() => {
                this.isLoading.set(false);
                this.hasError.set(true);
            });
    }

    finalize(form: FormGroup, accordionBlock: Block) {
        form.updateValueAndValidity();
        form.markAllAsTouched();
        if (!form.valid || this.hasQuestionBlockValidationError(accordionBlock)) {
            return;
        }
        const submissionEffect = this.submissionEffect();
        if (!submissionEffect) {
            this.hasError.set(false);
            this.showResults.emit();
            return;
        }
        this.isLoading.set(true);
        submissionEffect(this.contextModel.currentState)
            .then(() => {
                this.isLoading.set(false);
                this.hasError.set(false);
                this.showResults.emit();
                this.changeDetectorRef.detectChanges();
            })
            .catch(() => {
                this.isLoading.set(false);
                this.hasError.set(true);
            });
    }

    getQuestionBlockValidationError(validations: DataValidation[] | undefined): DataValidation | null {
        const failedQuestionLevelValidation = validations?.find(
            // Validations without `targetTemplateSlot` are question-level (not scoped to a specific slot).
            v => !v.targetTemplateSlot && !this.contextModel.processJsonLogic(v.logicalCondition)
        );
        return failedQuestionLevelValidation || null;
    }

    isSlotVisible(slot: TemplateSlot): boolean {
        return !this.slotService.isSlotHidden(slot);
    }

    private hasQuestionBlockValidationError(accordionBlock: Block): boolean {
        const questionBlocks = this.service.getQuestionBlocks(accordionBlock);

        return questionBlocks.some(
            questionBlock =>
                questionBlock.blockType === BlockType.QuestionBlock &&
                !this.service.hiddenBlocks[questionBlock.id] &&
                this.hasVisibleSlots(questionBlock) &&
                !!this.getQuestionBlockValidationError(questionBlock.validations)
        );
    }

    private hasVisibleSlots(block: Block): boolean {
        return !!block.template?.slots?.some(s => !this.contextModel.processJsonLogic(s.isHidden!));
    }

    private hasAnyQuestionBlockValidationError(): boolean {
        const containerBlocks = this.service.getContainerBlocks();
        for (const container of containerBlocks) {
            if (this.service.hiddenContainers[container.id]) {
                continue;
            }
            const accordionBlocks = this.service.getAccordionBlocks(container);
            for (const accordion of accordionBlocks) {
                if (this.service.hiddenAccordions[accordion.id]) {
                    continue;
                }
                if (this.hasQuestionBlockValidationError(accordion)) {
                    return true;
                }
            }
        }
        return false;
    }

    private init(): void {
        this.contextModel.init({ ...this.initialContextModel(), dataSources: {} });
        this.service.loadConfiguration(this.configuration());
    }
}
